import { forwardRef } from "react";

import { cn } from "@/shared/lib/utils";

import { InputOTP, InputOTPGroup, InputOTPSlot } from "../primitives/input-otp";

export interface ColocaOtpInputProps {
  value: string;
  onValueChange: (value: string) => void;
  length?: number;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
  onComplete?: (value: string) => void;
  /**
   * Draw a dot instead of each digit, for a secret such as the security PIN. The value itself is unchanged;
   * only what is painted in the slots is.
   */
  masked?: boolean;
  className?: string;
  "aria-label"?: string;
}

/**
 * Slots are elastic with a ceiling, not a fixed width. Six 44px boxes plus their gaps demand 304px, which no
 * phone affords once the shell, the page and a card have taken their padding — the row then overflowed its
 * container and the last box was clipped away by an ancestor's `overflow-hidden`. `flex-1` lets them share
 * whatever is there and `max-w-*` holds the original size the moment it fits, so every layout from `sm` up
 * renders exactly as before.
 *
 * Narrowing a slot costs no touch target: `InputOTPSlot` draws a `div`, and the focusable control is the
 * single hidden input stretched over the whole row, which keeps its full height.
 *
 * The `w-*` is still declared even though `flex-basis` decides the width: `cn()` only overrides the classes
 * it is given, so dropping it here would leave the primitive's own `w-9` alive in the final class list —
 * inert today because `flex-basis: 0` wins, and a silent 36px regression the day someone removes `flex-1`.
 */
const SIZE_CLASSES: Record<NonNullable<ColocaOtpInputProps["size"]>, string> = {
  sm: "h-10 w-9 min-w-0 flex-1 basis-0 max-w-9 text-sm sm:text-base",
  md: "h-12 w-11 min-w-0 flex-1 basis-0 max-w-11 text-base sm:text-lg",
  lg: "h-14 w-12 min-w-0 flex-1 basis-0 max-w-12 text-lg sm:text-2xl",
};

/** One filled dot per typed digit; it pops in so each keystroke still registers without showing the digit. */
const MASK_DOT = (
  <span
    aria-hidden="true"
    className="size-2.5 animate-jump-in rounded-full bg-foreground animate-duration-200 motion-reduce:animate-none"
  />
);

export const ColocaOtpInput = forwardRef<HTMLInputElement, ColocaOtpInputProps>(function ColocaOtpInput(
  {
    value,
    onValueChange,
    length = 6,
    size = "md",
    disabled = false,
    autoFocus = false,
    invalid = false,
    onComplete,
    masked = false,
    className,
    "aria-label": ariaLabel,
  },
  ref,
) {
  return (
    <InputOTP
      ref={ref}
      maxLength={length}
      value={value}
      onChange={onValueChange}
      onComplete={onComplete}
      disabled={disabled}
      autoFocus={autoFocus}
      pattern="[0-9]*"
      inputMode="numeric"
      autoComplete={masked ? "off" : "one-time-code"}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      containerClassName={cn("w-full justify-center", className)}
    >
      <InputOTPGroup className="flex w-full justify-center gap-1.5 sm:gap-2">
        {Array.from({ length }).map((_, i) => (
          <InputOTPSlot
            key={i}
            index={i}
            mask={masked ? MASK_DOT : undefined}
            className={cn(
              SIZE_CLASSES[size],
              "rounded-md! border! font-mono font-semibold tabular-nums",
              invalid && "border-destructive! ring-destructive/30!",
            )}
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
});
